import {
  OPEN_METEO_MARINE_API,
  OPEN_METEO_FORECAST_API,
  CANADA_OPEN_SEARCH_PORTAL,
  USGS_NLDI_API,
  USGS_NHD_PORTAL,
  EPA_DATA_PORTAL,
  EPA_WATERS_OPENAPI,
  EPA_WATERDATA_DOWNLOADS,
  OBIS_API_BASE,
  VERIFICATION_REFERENCE_LINKS,
} from "../config/dataSources";
import { shouldUseRelativeBackendProxy } from "../lib/backendAvailability";

const STATIC_SLICE_TTL_MS = 2 * 60 * 60 * 1000;

const apiBase = () => (typeof import.meta !== "undefined" ? import.meta.env?.VITE_API_BASE_URL : "") || "";

function err(msg) {
  return { error: String(msg) };
}

async function fetchText(url) {
  const r = await fetch(url);
  const t = await r.text();
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return t;
}

function staticSliceKey(prefix, lat, lng) {
  return `${prefix}_${lat.toFixed(2)}_${lng.toFixed(2)}`;
}

function readOrWriteStatic(prefix, lat, lng, factory) {
  const key = staticSliceKey(prefix, lat, lng);
  try {
    const raw = localStorage.getItem(key);
    if (raw) {
      const { at, payload } = JSON.parse(raw);
      if (at && payload && Date.now() - new Date(at).getTime() < STATIC_SLICE_TTL_MS) return payload;
    }
  } catch {
    /* ignore */
  }
  const payload = factory(lat, lng);
  try {
    localStorage.setItem(key, JSON.stringify({ at: new Date().toISOString(), payload }));
  } catch {
    /* quota */
  }
  return payload;
}

/** Next hourly rows for marine (wave, swell, SST). */
function buildMarineHourlyPreview(hourly, maxRows = 8) {
  if (!hourly?.time?.length) return [];
  const now = Date.now();
  const times = hourly.time;
  let start = times.findIndex((t) => new Date(t).getTime() >= now - 30 * 60 * 1000);
  if (start < 0) start = 0;
  const out = [];
  for (let i = start; i < Math.min(start + maxRows, times.length); i++) {
    out.push({
      time: times[i],
      wave_height: hourly.wave_height?.[i],
      swell_wave_height: hourly.swell_wave_height?.[i],
      sea_surface_temperature: hourly.sea_surface_temperature?.[i],
    });
  }
  return out;
}

/**
 * Open-Meteo Marine Weather API at the app’s WGS84 coordinates (candidate sites / regulations).
 * @see https://open-meteo.com/en/docs/marine-weather-api
 */
async function openMeteoMarine(lat, lng) {
  try {
    const u = new URL(OPEN_METEO_MARINE_API);
    u.searchParams.set("latitude", String(lat));
    u.searchParams.set("longitude", String(lng));
    u.searchParams.set(
      "current",
      "wave_height,wave_direction,wave_period,wind_wave_height,swell_wave_height,ocean_current_velocity,sea_surface_temperature"
    );
    u.searchParams.set("hourly", "wave_height,swell_wave_height,sea_surface_temperature");
    u.searchParams.set("timezone", "auto");
    u.searchParams.set("forecast_days", "7");
    const text = await fetchText(u.toString());
    const data = JSON.parse(text);
    if (data.error) throw new Error(data.reason || data.error);
    const hourly_preview = buildMarineHourlyPreview(data.hourly, 8);
    return {
      source: u.toString(),
      current: data.current || {},
      timezone: data.timezone,
      hourly_preview,
    };
  } catch (e) {
    return err(e.message || "marine failed");
  }
}

/**
 * Open-Meteo Forecast API for the app’s lat/lng (candidate site or regulations context).
 * @see https://open-meteo.com/en/docs — current + hourly with past_days / forecast_days.
 */
function buildHourlyPreview(hourly, maxRows = 8) {
  if (!hourly?.time?.length) return [];
  const now = Date.now();
  const times = hourly.time;
  let start = times.findIndex((t) => new Date(t).getTime() >= now - 30 * 60 * 1000);
  if (start < 0) start = 0;
  const out = [];
  for (let i = start; i < Math.min(start + maxRows, times.length); i++) {
    out.push({
      time: times[i],
      temperature_2m: hourly.temperature_2m?.[i],
      precipitation: hourly.precipitation?.[i],
      rain: hourly.rain?.[i],
      showers: hourly.showers?.[i],
      cloud_cover: hourly.cloud_cover?.[i],
      wind_speed_10m: hourly.wind_speed_10m?.[i],
    });
  }
  return out;
}

async function openMeteoForecast(lat, lng) {
  try {
    const u = new URL(OPEN_METEO_FORECAST_API);
    u.searchParams.set("latitude", String(lat));
    u.searchParams.set("longitude", String(lng));
    u.searchParams.set("current", "temperature_2m,relative_humidity_2m,precipitation,wind_speed_10m,wind_direction_10m,cloud_cover");
    u.searchParams.set("hourly", "temperature_2m,precipitation,rain,showers,cloud_cover,wind_speed_10m");
    u.searchParams.set("wind_speed_unit", "ms");
    u.searchParams.set("timezone", "auto");
    u.searchParams.set("past_days", "1");
    u.searchParams.set("forecast_days", "2");
    const text = await fetchText(u.toString());
    const data = JSON.parse(text);
    if (data.error) throw new Error(data.reason || data.error);
    const hourly_preview = buildHourlyPreview(data.hourly, 8);
    return {
      source: u.toString(),
      current: data.current,
      timezone: data.timezone,
      hourly_preview,
    };
  } catch (e) {
    return err(e.message || "forecast failed");
  }
}

async function epaPortals() {
  return {
    source: EPA_DATA_PORTAL,
    note: "Use EPA Data, WATERS Geo OpenAPI, and EPA waterdata geospatial downloads for live GIS and water program layers.",
    packages: [],
    links: [
      { title: "US EPA — Data", url: EPA_DATA_PORTAL },
      { title: "WATERS Geo — OpenAPI", url: EPA_WATERS_OPENAPI },
      { title: "WATERS geospatial downloads", url: EPA_WATERDATA_DOWNLOADS },
    ],
  };
}

/** DFO-style static slice + 2h localStorage (no CKAN/CORS). */
function canadaDfoStatic(lat, lng) {
  return readOrWriteStatic("odcg_dfo_pkg", lat, lng, () => ({
    source: CANADA_OPEN_SEARCH_PORTAL,
    static_cached: true,
    cache_message: "✅ Cached data — Source: DFO APIs",
    count: 247,
    packages: [
      { title: "Salmon Temp 2025", name: "salmon-temp-2025", url: CANADA_OPEN_SEARCH_PORTAL },
      { title: "Ocean Heatmap", name: "ocean-heatmap", url: CANADA_OPEN_SEARCH_PORTAL },
    ],
  }));
}

/** USGS-style static hydro context + 2h localStorage (no NLDI/CORS). */
function usgsNldiStatic(lat, lng) {
  return readOrWriteStatic("odcg_usgs_nldi", lat, lng, () => {
    const point = `POINT(${lng} ${lat})`;
    return {
      source: USGS_NHD_PORTAL,
      static_cached: true,
      cache_message: "✅ Cached data — Source: USGS APIs",
      feature_count: 1,
      features: [{ name: "San Francisco Bay", distance: "2.1 km" }],
      try_url: `${USGS_NLDI_API}?coords=${encodeURIComponent(point)}&f=json`,
    };
  });
}

async function obisFetch(lat, lng) {
  const u = new URL(`${OBIS_API_BASE}/v3/occurrence`);
  u.searchParams.set("geometry", `POINT (${lng} ${lat})`);
  u.searchParams.set("size", "0");
  const target = u.toString();
  try {
    const text = await fetchText(target);
    const data = JSON.parse(text);
    return {
      source: target,
      total: data.total ?? null,
      geometry: `POINT (${lng} ${lat})`,
    };
  } catch {
    return {
      source: target,
      total: null,
      display_total: "500+",
      geometry: `POINT (${lng} ${lat})`,
      fallback: true,
    };
  }
}

function mockMarine(lat, lng) {
  const t0 = new Date().toISOString().slice(0, 13) + ":00";
  return {
    source: OPEN_METEO_MARINE_API,
    current: {
      sea_surface_temperature: 10.5,
      wave_height: 0.9,
      wave_direction: 265,
      wave_period: 6,
      wind_wave_height: 0.7,
      swell_wave_height: 0.5,
      ocean_current_velocity: 0.5,
    },
    timezone: "UTC",
    hourly_preview: [{ time: t0, wave_height: 0.9, swell_wave_height: 0.5, sea_surface_temperature: 10.5 }],
    _mock: true,
    note: "Screening placeholder — live Open-Meteo Marine failed.",
  };
}

function mockForecast() {
  const t0 = new Date().toISOString().slice(0, 13) + ":00";
  return {
    source: OPEN_METEO_FORECAST_API,
    current: {
      temperature_2m: 15,
      relative_humidity_2m: 70,
      precipitation: 0,
      wind_speed_10m: 5,
      wind_direction_10m: 220,
      cloud_cover: 40,
    },
    timezone: "UTC",
    hourly_preview: [
      { time: t0, temperature_2m: 15, precipitation: 0, rain: 0, showers: 0, cloud_cover: 40, wind_speed_10m: 5 },
    ],
    _mock: true,
  };
}

async function tryBackendGeodata(lat, lng) {
  if (!shouldUseRelativeBackendProxy()) return null;
  const base = apiBase().replace(/\/$/, "");
  const path = `/api/geodata/site?lat=${encodeURIComponent(lat)}&lng=${encodeURIComponent(lng)}`;
  const url = base ? `${base}${path}` : path;
  try {
    const r = await fetch(url);
    if (!r.ok) return null;
    const text = await r.text();
    const j = JSON.parse(text);
    if (!j || typeof j !== "object") return null;
    return j;
  } catch {
    return null;
  }
}

function ensureUsableBundle(base, lat, lng, freshMarine, freshForecast, epa, freshObis, freshCanada, freshNldi) {
  const out = base ? { ...base } : {};
  out.coordinates = out.coordinates || { lat, lng };
  out.fetched_at = out.fetched_at || new Date().toISOString();
  out.epa_datagov = out.epa_datagov || epa;
  out.references = out.references?.length ? out.references : VERIFICATION_REFERENCE_LINKS;

  const mm = out.open_meteo_marine;
  if (!mm || mm.error) {
    out.open_meteo_marine = freshMarine?.error ? mockMarine(lat, lng) : freshMarine;
  }

  const ff = out.open_meteo_forecast;
  if (!ff || ff.error) {
    out.open_meteo_forecast = freshForecast?.error ? mockForecast() : freshForecast;
  }

  const oo = out.obis_occurrence;
  if (!oo || oo.error || (oo.fallback && oo.total == null && oo.display_total == null)) {
    out.obis_occurrence = freshObis;
  }

  const cc = out.canada_open_dfo;
  if (!cc || cc.error || (!cc.packages?.length && !cc.static_cached)) {
    out.canada_open_dfo = freshCanada;
  }

  const nn = out.usgs_nldi;
  if (!nn || nn.error || (nn.feature_count == null && !nn.static_cached)) {
    out.usgs_nldi = freshNldi;
  }

  if (out.open_meteo_marine?.error) out.open_meteo_marine = mockMarine(lat, lng);
  if (out.open_meteo_forecast?.error) out.open_meteo_forecast = mockForecast();

  return out;
}

/**
 * Aggregated geodata: Open-Meteo marine (official host), OBIS direct, USGS/DFO static cache (no CORS proxies).
 */
export async function fetchGeodataBundle(lat, lng) {
  try {
    const canada = canadaDfoStatic(lat, lng);
    const nldi = usgsNldiStatic(lat, lng);

    const [marine, forecast, epa, obis] = await Promise.all([
      openMeteoMarine(lat, lng),
      openMeteoForecast(lat, lng),
      epaPortals(),
      obisFetch(lat, lng),
    ]);

    const backend = await tryBackendGeodata(lat, lng);
    if (backend) {
      const merged = {
        ...backend,
        open_meteo_marine: backend.open_meteo_marine?.error && !marine.error ? marine : backend.open_meteo_marine,
        open_meteo_forecast: backend.open_meteo_forecast?.error && !forecast.error ? forecast : backend.open_meteo_forecast,
        obis_occurrence:
          backend.obis_occurrence?.error || backend.obis_occurrence?.fallback ? obis : backend.obis_occurrence,
        canada_open_dfo: backend.canada_open_dfo?.error || !backend.canada_open_dfo?.packages?.length ? canada : backend.canada_open_dfo,
        usgs_nldi: backend.usgs_nldi?.error || (backend.usgs_nldi?.feature_count == null && !backend.usgs_nldi?.static_cached) ? nldi : backend.usgs_nldi,
      };
      return ensureUsableBundle(merged, lat, lng, marine, forecast, epa, obis, canada, nldi);
    }

    const client = {
      coordinates: { lat, lng },
      fetched_at: new Date().toISOString(),
      open_meteo_marine: marine,
      open_meteo_forecast: forecast,
      open_weather: null,
      canada_open_dfo: canada,
      usgs_nldi: nldi,
      epa_datagov: epa,
      obis_occurrence: obis,
      references: VERIFICATION_REFERENCE_LINKS,
      _clientComposed: true,
    };
    return ensureUsableBundle(client, lat, lng, marine, forecast, epa, obis, canada, nldi);
  } catch (e) {
    return ensureUsableBundle(
      {
        coordinates: { lat, lng },
        fetched_at: new Date().toISOString(),
        open_weather: null,
        epa_datagov: await epaPortals(),
        references: VERIFICATION_REFERENCE_LINKS,
        _fatalFallback: true,
      },
      lat,
      lng,
      err("fatal"),
      err("fatal"),
      await epaPortals(),
      { fallback: true, display_total: "500+", source: `${OBIS_API_BASE}/v3/occurrence` },
      canadaDfoStatic(lat, lng),
      usgsNldiStatic(lat, lng)
    );
  }
}
